// Lamp design serializer/deserializer
// Version: 1.0.0

const CURRENT_VERSION = '1.0.0';

/**
 * Gets the current git commit hash
 * @returns {Promise<string>} The current commit hash
 */
async function getCommitHash() {
  return "placeholder";
}

/**
 * Serializes a lamp design to JSON
 * @param {Object} lampDesign - The lamp design object
 * @returns {Promise<string>} JSON string representation of the lamp design
 */
export async function serializeLampDesign(lampDesign) {
  const commitHash = await getCommitHash();
  
  // Ensure all required fields are present
  if (!lampDesign.baseShape || !lampDesign.baseShape.diameter || !lampDesign.baseShape.height || !lampDesign.baseShape.tubularSegments || !lampDesign.baseShape.radialSegments || !lampDesign.baseShape.profilePoints) {
    throw new Error('Invalid lamp design: missing required base shape data');
  }
  
  
  const serialized = {
    version: CURRENT_VERSION,
    metadata: {
      name: lampDesign.name || 'Untitled Lamp',
      description: lampDesign.description || '',
      createdAt: lampDesign.createdAt || new Date().toISOString(),
      lastModified: new Date().toISOString(),
      commitHash: commitHash
    },
    baseShape: {
      diameter: lampDesign.baseShape.diameter,
      height: lampDesign.baseShape.height,
      tubularSegments: lampDesign.baseShape.tubularSegments,
      radialSegments: lampDesign.baseShape.radialSegments,
      profilePoints: lampDesign.baseShape.profilePoints
    },
    modifiers: lampDesign.modifiers.map(modifier => ({
      type: modifier.type,
      disabled: modifier.disabled,
      is_symmetrical: modifier.is_symmetrical,
      parameters: modifier.parameters
    })),
    noiseSeed: lampDesign.noiseSeed
  };

  return JSON.stringify(serialized, null, 2);
}

/**
 * Deserializes a JSON string into a lamp design object
 * @param {string} jsonString - The JSON string to deserialize
 * @returns {Object} The deserialized lamp design object
 * @throws {Error} If the JSON is invalid or version is incompatible
 */
export function deserializeLampDesign(jsonString) {
  const data = JSON.parse(jsonString);
  
  // Version compatibility check
  if (!data.version) {
    throw new Error('Invalid lamp design: missing version');
  }
  
  const [major] = data.version.split('.');
  const [currentMajor] = CURRENT_VERSION.split('.');
  
  if (major !== currentMajor) {
    throw new Error(`Incompatible lamp design version: ${data.version}. Current version: ${CURRENT_VERSION}`);
  }

  // Check for missing required fields
  const missingFields = [];
  if (!data.baseShape) missingFields.push('baseShape');
  if (!data.modifiers) missingFields.push('modifiers');

  if (missingFields.length > 0) {
    console.error('Missing required fields:', missingFields);
    throw new Error(`Invalid lamp design: missing required fields: ${missingFields.join(', ')}`);
  }

  // Return the data with its original structure
  return {
    version: data.version,
    metadata: data.metadata,
    baseShape: data.baseShape,
    modifiers: data.modifiers,
    noiseSeed: data.noiseSeed
  };
}

/**
 * Helper function to find differences between objects
 * @param {Object} obj1 - First object to compare
 * @param {Object} obj2 - Second object to compare
 * @param {string} path - Current path in the object tree
 * @returns {Array} Array of difference descriptions
 */
function findObjectDifferences(obj1, obj2, path = '') {
  const differences = [];
  
  // Get all unique keys from both objects
  const allKeys = new Set([...Object.keys(obj1 || {}), ...Object.keys(obj2 || {})]);
  
  for (const key of allKeys) {
    const currentPath = path ? `${path}.${key}` : key;
    const val1 = obj1?.[key];
    const val2 = obj2?.[key];
    
    if (val1 === val2) continue;
    
    if (typeof val1 !== typeof val2) {
      differences.push(`${currentPath}: type mismatch (${typeof val1} vs ${typeof val2})`);
      continue;
    }
    
    if (val1 === null || val2 === null || val1 === undefined || val2 === undefined) {
      differences.push(`${currentPath}: ${JSON.stringify(val1)} !== ${JSON.stringify(val2)}`);
      continue;
    }
    
    if (Array.isArray(val1) && Array.isArray(val2)) {
      if (val1.length !== val2.length) {
        differences.push(`${currentPath}: array length mismatch (${val1.length} vs ${val2.length})`);
      } else {
        for (let i = 0; i < val1.length; i++) {
          if (typeof val1[i] === 'object' && typeof val2[i] === 'object') {
            differences.push(...findObjectDifferences(val1[i], val2[i], `${currentPath}[${i}]`));
          } else if (val1[i] !== val2[i]) {
            differences.push(`${currentPath}[${i}]: ${JSON.stringify(val1[i])} !== ${JSON.stringify(val2[i])}`);
          }
        }
      }
      continue;
    }
    
    if (typeof val1 === 'object' && typeof val2 === 'object') {
      differences.push(...findObjectDifferences(val1, val2, currentPath));
      continue;
    }
    
    differences.push(`${currentPath}: ${JSON.stringify(val1)} !== ${JSON.stringify(val2)}`);
  }
  
  return differences;
}

/**
 * Validates serialization/deserialization integrity by comparing core fields
 * @param {Object} originalDesign - The original lamp design object
 * @param {Object} deserializedDesign - The deserialized lamp design object
 * @returns {Object} { isValid: boolean, differences: Array }
 */
export function validateSerializationIntegrity(originalDesign, deserializedDesign) {
  // Compare only core data fields, ignoring metadata added during serialization
  const originalCore = {
    baseShape: originalDesign.baseShape,
    modifiers: originalDesign.modifiers,
    noiseSeed: originalDesign.noiseSeed
  };
  
  const deserializedCore = {
    baseShape: deserializedDesign.baseShape,
    modifiers: deserializedDesign.modifiers,
    noiseSeed: deserializedDesign.noiseSeed
  };
  
  const differences = findObjectDifferences(originalCore, deserializedCore);
  const isValid = differences.length === 0;
  
  return {
    isValid,
    differences,
    originalCore,
    deserializedCore
  };
}

/**
 * Performs a complete serialization round-trip test and validation
 * @param {Object} lampDesign - The lamp design to test
 * @returns {Promise<Object>} { success: boolean, deserializedDesign?: Object, error?: string, differences?: Array }
 */
export async function testSerializationRoundTrip(lampDesign) {
  try {
    // Serialize the design
    const serializedDesign = await serializeLampDesign(lampDesign);
    
    // Deserialize it back
    const deserializedDesign = deserializeLampDesign(serializedDesign);
    
    // Validate integrity
    const validation = validateSerializationIntegrity(lampDesign, deserializedDesign);
    
    if (validation.isValid) {
      return {
        success: true,
        deserializedDesign
      };
    } else {
      return {
        success: false,
        error: 'Serialization integrity check failed',
        differences: validation.differences,
        originalCore: validation.originalCore,
        deserializedCore: validation.deserializedCore
      };
    }
  } catch (error) {
    return {
      success: false,
      error: error.message
    };
  }
} 